import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Input } from 'shared-components';
import { PROVINCES } from 'shared-components/src/utils/provinces';
import { formatCardNumber } from '../../../../utils/cardFormatting';
import { CONTACT_COUNTRY, formatPostalCode, toProvinceKey } from '../../../../utils/contactCard';
import { FieldError } from '../../../Auth/styles';
import { FieldHint, ContactDropdown } from './styles';

// Fields shared by the Add/Edit debit card contact dialogs. The inputs work with either a
// ref (uncontrolled) or value/onChange (controlled): the formatted text is written back to
// the input before the dialog's own onChange sees it.

const formatOnChange = (format, onChange) => e => {
  e.target.value = format(e.target.value);
  if (onChange) onChange(e);
};

// Grouped in fours as typed, so a number copied with the spaces printed on the card is fine.
// `direction` says whose card it is: the one funds go to (push) or come from (pull).
export const CardNumberInput = forwardRef(({ id, direction, onChange, ...props }, ref) => {
  const intl = useIntl();

  return (
    <>
      <Input
        id={id}
        ref={ref}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        aria-describedby={`${id}-hint`}
        onChange={formatOnChange(formatCardNumber, onChange)}
        {...props}
      />
      <FieldHint id={`${id}-hint`}>
        {intl.messages[`contact-fields-card-number-hint-${direction}`]}
      </FieldHint>
    </>
  );
});

CardNumberInput.propTypes = {
  id: PropTypes.string.isRequired,
  direction: PropTypes.oneOf(['push', 'pull']).isRequired,
  onChange: PropTypes.func,
};

CardNumberInput.defaultProps = {
  onChange: undefined,
};

// Upper case with a space as typed, with the expected format shown until typing starts.
export const PostalCodeInput = forwardRef(({ onChange, ...props }, ref) => (
  <Input
    ref={ref}
    type="text"
    placeholder="A1A 1A1"
    autoComplete="postal-code"
    onChange={formatOnChange(formatPostalCode, onChange)}
    {...props}
  />
));

PostalCodeInput.propTypes = {
  onChange: PropTypes.func,
};

PostalCodeInput.defaultProps = {
  onChange: undefined,
};

// Chosen from the shared province list, so it cannot be typed in the wrong format. Each is
// shown by its name in the portal's language with its code, e.g. "Québec (QC)"; the code is
// what is sent.
export const ProvinceSelect = ({ id, value, onChange, invalid }) => {
  const intl = useIntl();
  const selected = toProvinceKey(value);
  const options = PROVINCES.map(({ key }) => ({
    key,
    val: `${intl.messages[`contact-fields-province-${key}`]} (${key})`,
  }));

  return (
    <ContactDropdown
      id={id}
      options={options}
      selected={selected || null}
      caption={intl.messages['select-placeholder-option']}
      updateItem={onChange}
      maxHeightPx={200}
      aria-invalid={invalid || undefined}
    />
  );
};

ProvinceSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  invalid: PropTypes.bool,
};

ProvinceSelect.defaultProps = {
  value: '',
  invalid: false,
};

// Canada is the only country accepted today, so it is the only choice and starts selected.
export const CountrySelect = ({ id }) => {
  const intl = useIntl();

  return (
    <ContactDropdown
      id={id}
      options={[{ key: CONTACT_COUNTRY, val: intl.messages['contact-fields-country-canada'] }]}
      selected={CONTACT_COUNTRY}
      updateItem={() => {}}
    />
  );
};

CountrySelect.propTypes = {
  id: PropTypes.string.isRequired,
};

// The message under a field that failed, in the portal's language. `error` is an i18n key.
export const ContactFieldError = ({ id, error }) => {
  const intl = useIntl();
  if (!error) return null;

  return (
    <FieldError id={id} className="invalid-feedback">
      {intl.messages[error] || error}
    </FieldError>
  );
};

ContactFieldError.propTypes = {
  id: PropTypes.string,
  error: PropTypes.string,
};

ContactFieldError.defaultProps = {
  id: undefined,
  error: undefined,
};
